// /max: metadata completa — mesma precisão do `phonenumbers` do back
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import type { CountryCode } from 'libphonenumber-js/max';

/**
 * wa_id legado BR: conta WhatsApp anterior ao nono dígito —
 * 55 + DDD + 8 dígitos. Inválido pra libphonenumber, mas é um
 * número real no banco (mesma regra do PhoneNumberStr do back).
 */
const LEGACY_BR_WA_ID = /^55(\d{2})(\d{4})(\d{4})$/;

export interface PhoneDisplay {
    formatted: string;
    country?: CountryCode;
}

export const parsePhoneDisplay = (phoneNumber: string): PhoneDisplay => {
    if (!phoneNumber) return { formatted: phoneNumber, country: undefined };

    const digits = phoneNumber.replace(/\D/g, '');
    const parsed = parsePhoneNumberFromString(`+${digits}`);
    if (parsed?.isValid()) {
        return { formatted: parsed.formatInternational(), country: parsed.country };
    }

    const legacy = LEGACY_BR_WA_ID.exec(digits);
    if (legacy) {
        const [, ddd, prefix, suffix] = legacy;
        return { formatted: `+55 (${ddd}) ${prefix}-${suffix}`, country: 'BR' };
    }

    return { formatted: `+${digits}`, country: undefined };
};

export interface PhoneOrigin {
    country?: CountryCode;
    /** Código de área BR (DDD) — só definido quando country === 'BR'. */
    ddd?: string;
}

/** Origem geográfica do número: país (ISO alpha-2) + DDD quando BR. Mesmos fallbacks do parsePhoneDisplay. */
export const parsePhoneOrigin = (phoneNumber: string): PhoneOrigin => {
    if (!phoneNumber) return {};

    const digits = phoneNumber.replace(/\D/g, '');
    const parsed = parsePhoneNumberFromString(`+${digits}`);
    if (parsed?.isValid()) {
        const ddd = parsed.country === 'BR' ? String(parsed.nationalNumber).slice(0, 2) : undefined;
        return { country: parsed.country, ddd };
    }

    const legacy = LEGACY_BR_WA_ID.exec(digits);
    if (legacy) return { country: 'BR', ddd: legacy[1] };

    return {};
};

export const formatPhoneNumber = (phoneNumber: string) => {
    return parsePhoneDisplay(phoneNumber).formatted;
};

export const cleanPhoneNumber = (phoneNumber: string) => {
    return phoneNumber.replace(/\D/g, '');
};
